import AppBar from "@material-ui/core/AppBar";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import Divider from "@material-ui/core/Divider";
import IconButton from "@material-ui/core/IconButton";
import { createMuiTheme, makeStyles } from "@material-ui/core/styles";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { ThemeProvider } from "@material-ui/styles";
import { withSnackbar } from "notistack";
import React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import CustomButton from "../Buttons/Custom";
import ErrorScreen from "../Error";
import MessageGroup from "../ListItems/MessageGroup";
import * as actions from "../../store/actions";
import getDefaultFormattedTime from "../../utils/getDefaultFormattedTime";
import Composer from "./Composer";
import resolveResponse from "../../utils/resolveResponse";
import getAppUser from "../../store/selectors/getAppUser";

const ResponseMessaging = ({
  property,
  history,
  appUser,
  enqueueSnackbar,
  ...props
}) => {
  const [resolvePending, setResolvePending] = React.useState(false);
  const [newMessagesStartingAt, setNewMessagesStartingAt] = React.useState(
    null
  );
  const [alertVisible, setAlertVisible] = React.useState(false);

  React.useEffect(() => {
    if (props.instanceId) {
      const response = props.response;
      if (response) {
        props.dispatch(
          actions.properties.setSelectedInstance(props.instanceId)
        );
        const propertyEvents = Object.values(
          response.propertyEvents.byId
        ).reverse();
        let unreadMessagesStartingAt = null;
        let index = 0;
        let iterator = propertyEvents[index];
        while (unreadMessagesStartingAt === null && iterator !== undefined) {
          if (iterator.createdAt > response.lastVisited) {
            unreadMessagesStartingAt = iterator.eventId;
          }
          index = index + 1;
          iterator = propertyEvents[index];
        }
        setNewMessagesStartingAt(unreadMessagesStartingAt);
      }
    } else {
      setNewMessagesStartingAt(null);
    }
    return () => props.dispatch(actions.properties.setSelectedInstance(null));
  }, [props.instanceId]);

  const handleBackPress = () => {
    history.push("/map");
  };

  const classes = useStyles();
  if (props.response) {
    const onResolveResponse = async () => {
      setAlertVisible(false);
      setResolvePending(true);
      try {
        await resolveResponse({
          property,
          appUser,
          navigate: () => history.push(`/map`),
        });
      } catch (e) {
        enqueueSnackbar(e, { variant: "error" });
      }
      setResolvePending(false);
    };

    const resendMessage = (propertyEvent) => {
      // Remove client-side attributes before attempting submission
      props.dispatch(
        actions.properties.createPropertyEvent({
          ...propertyEvent,
          pending: undefined,
          error: undefined,
        })
      );
    };

    const messageGroups = getMessageGroups({
      response: props.response,
      newMessagesStartingAt,
      retryHandler: resendMessage,
    });
    return (
      <>
        <div className={classes.root}>
          <ThemeProvider
            theme={createMuiTheme({
              palette: {
                primary: {
                  main: "#fff",
                },
              },
            })}
          >
            <AppBar position="static">
              <Toolbar>
                <IconButton
                  edge="start"
                  className={classes.backButton}
                  color="inherit"
                  aria-label="go-back"
                  onClick={handleBackPress}
                >
                  <ChevronRightIcon />
                </IconButton>
                <Typography variant="h6" color="inherit" noWrap>
                  {props.propertyDisplayName}
                </Typography>
                <div className={classes.grow} />
                <Button
                  className={classes.appBarButton}
                  onClick={() =>
                    history.push(`/map/properties/${property.propertyId}`)
                  }
                >
                  Details
                </Button>
              </Toolbar>
            </AppBar>
          </ThemeProvider>
          <div className={classes.scrollableMessages}>
            <div className={classes.messages}>
              {messageGroups.map((messageGroup) => (
                <MessageGroup
                  key={messageGroup.key}
                  userId={messageGroup.userId}
                  messages={messageGroup.messages}
                  date={messageGroup.date}
                  newMessagesIndex={messageGroup.newMessagesIndex}
                />
              ))}
              {props.sendingMessage && (
                <div className={classes.progress}>
                  <CircularProgress size={18} />
                </div>
              )}
              {props.responseActive && (
                <div className={classes.actions}>
                  <CustomButton
                    variant="contained"
                    loading={resolvePending}
                    text="Resolve"
                    onClick={() => setAlertVisible(true)}
                  />
                </div>
              )}
            </div>
          </div>
          <Divider />
          <Composer
            propertyId={property.propertyId}
            instanceId={props.instanceId}
          />
        </div>
        <Dialog open={alertVisible} onClose={() => setAlertVisible(false)}>
          <DialogContent>
            <DialogContentText className={classes.alertMessage}>
              Are you sure you want to resolve this response?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button color="primary" onClick={() => setAlertVisible(false)}>
              Cancel
            </Button>
            <Button color="primary" onClick={onResolveResponse}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  } else {
    return <ErrorScreen onButtonClick={() => history.goBack()} />;
  }
};

const getMessageGroups = ({
  response,
  newMessagesStartingAt,
  retryHandler,
}) => {
  const messageGroups = [];
  let currentMessageGroup;
  let currentPropertyEventTimestamp;
  const eventIds = Object.keys(response.propertyEvents.byId);
  for (const eventId of eventIds) {
    const propertyEvent = response.propertyEvents.byId[eventId];
    const propertyEventTimestamp = propertyEvent.createdAt;
    if (currentMessageGroup === undefined) {
      currentMessageGroup = {
        key: eventId,
        userId: propertyEvent.senderId,
        messages: [],
        date: getDefaultFormattedTime(propertyEventTimestamp),
      };
    } else if (currentMessageGroup.userId !== propertyEvent.senderId) {
      messageGroups.push(currentMessageGroup);
      currentMessageGroup = {
        key: eventId,
        userId: propertyEvent.senderId,
        messages: [],
        date:
          currentPropertyEventTimestamp !== undefined &&
          Math.abs(propertyEventTimestamp - currentPropertyEventTimestamp) >
            3600000 /* 1 hour */
            ? getDefaultFormattedTime(propertyEventTimestamp)
            : undefined,
      };
    } else if (
      currentPropertyEventTimestamp !== undefined &&
      Math.abs(propertyEventTimestamp - currentPropertyEventTimestamp) >
        3600000 /* 1 hour */
    ) {
      messageGroups.push(currentMessageGroup);
      currentMessageGroup = {
        key: eventId,
        userId: propertyEvent.senderId,
        messages: [],
        date: getDefaultFormattedTime(propertyEventTimestamp),
      };
    }
    currentPropertyEventTimestamp = propertyEventTimestamp;
    const message = {
      id: eventId,
      text: propertyEvent.text,
      timestamp: propertyEvent.createdAt,
      pending: propertyEvent.pending,
      error: propertyEvent.error,
      onErrorClick: () => retryHandler(propertyEvent),
    };
    currentMessageGroup.messages.push(message);
    if (eventId === newMessagesStartingAt) {
      currentMessageGroup.newMessagesIndex = currentMessageGroup.messages.indexOf(
        message
      );
    }
  }
  // Have to add the last message group that was being created to the array!
  if (currentMessageGroup) {
    messageGroups.push(currentMessageGroup);
  }
  return messageGroups;
};

const useStyles = makeStyles((theme) => ({
  grow: {
    flexGrow: 1,
  },
  root: {
    minHeight: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  backButton: {
    marginRight: 8,
  },
  appBarButton: {
    color: theme.palette.primary.main,
  },
  scrollableMessages: {
    display: "flex",
    flex: 1,
    flexDirection: "column-reverse",
    overflowY: "auto",
  },
  messages: {
    paddingBottom: 8,
    paddingTop: 8,
  },
  input: {
    marginBottom: 8,
    marginLeft: 8,
    marginRight: 8,
    marginTop: 8,
  },
  actions: {
    display: "flex",
    flexDirection: "row",
    marginLeft: 8,
    marginRight: 8,
    marginTop: 16,
  },
  extraAction: {
    marginLeft: 8,
  },
  buttonProgress: {
    marginRight: 8,
  },
  progress: {
    display: "flex",
    justifyContent: "flex-end",
    marginRight: 8,
    marginTop: 8,
  },
  alertMessage: {
    marginTop: 12,
  },
}));

const mapStateToProps = (state, ownProps) => {
  const appUser = state.users.byId[state.auth.user.username];
  let appUserDisplayName;
  let serviceProviderId;
  if (appUser) {
    appUserDisplayName = appUser.displayName;
    serviceProviderId = appUser.serviceProviderId;
  }
  const response = state.properties.instances.byId[ownProps.instanceId];
  let property;
  let propertyDisplayName;
  let responseActive;
  if (response) {
    property = state.properties.byId[response.propertyId];
    if (property) {
      propertyDisplayName = property.displayName;
      responseActive = !!property.instanceId;
    }
  }
  return {
    appUserId: state.auth.user.username,
    appUserDisplayName,
    serviceProviderId,
    response,
    responseActive,
    property,
    propertyDisplayName,
    sendingMessage: state.properties.instances.loading,
    appUser: getAppUser(state),
  };
};

export default withSnackbar(
  connect(mapStateToProps)(withRouter(ResponseMessaging))
);
